import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import { getActiveQuotationLossReasonOptions } from '../placeholders/commercialCatalogDefaults';
import {
  buildLossCompetitorLineMap,
  buildLossCompetitorPayload,
  calcAggregateVariationPct,
  calcCompetitorLineSubtotal,
  calcOfferTotals,
  calcPriceVariationPct,
  formatLossCompetitorMoney,
  formatVariationPct,
  lossCompetitorHasData,
  parseLossCompetitor,
  resolveCompetitorSelection,
} from '../placeholders/quotationLossCompetitorUtils';
import QuotationCloseWizard from './QuotationCloseWizard';
import QuotationVendidaCloseWizard from './QuotationVendidaCloseWizard';
import { buildQuotationPdfObjectUrl } from '../../utils/quotationPdf';
import { QUOTATION_PREVIEW_SIDE_PANEL_WIDTH } from '../../constants/quotationPreviewPanel';
import {
  buildDefaultLineSelections,
  buildClearedLineSelections,
  partitionQuotationLineItems,
} from '../placeholders/quotationItemClassification';
import {
  createProductionRequestsFromQuotationClose,
  createUpsellingPurchaseRequestsFromQuotationClose,
} from '../placeholders/quotationCloseFulfillmentUtils';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { CHILE_MOBILE_PLACEHOLDER, chileanMobileError, formatChileanMobile } from '../../utils/textUtils';

const CLOSE_STATUS_OPTIONS = [
  { value: 'Adjudicada', label: 'Adjudicada' },
  { value: 'Vendida', label: 'Vendida' },
  { value: 'Perdida', label: 'Perdida' },
  { value: 'Rechazada', label: 'Rechazada' },
  { value: 'Cancelada', label: 'Cancelada' },
  { value: 'Desierta', label: 'Desierta' },
];

const CLOSE_STATUS_DESCRIPTIONS = {
  Adjudicada: 'El cliente aceptó la cotización.',
  Vendida: 'Registrar la venta y los datos del pedido.',
  Perdida: 'La oportunidad no se concretó con esta oferta.',
  Rechazada: 'El cliente rechazó la cotización.',
  Cancelada: 'La cotización fue cancelada antes del cierre.',
  Desierta: 'La licitación o proceso quedó desierto.',
};

const FINAL_CLOSE_STATUSES = ['Vendida', 'Perdida', 'Rechazada', 'Cancelada', 'Desierta'];

const normalizeSummaryStatus = (value = '') => {
  const status = String(value || '').trim() || 'Emitida';
  return status === 'Aprobada' ? 'Adjudicada' : status;
};

const STATUS_STYLE = {
  Emitida: { bg: '#dbeafe', text: '#1e40af' },
  Adjudicada: { bg: '#dcfce7', text: '#166534' },
  Vendida: { bg: '#ccfbf1', text: '#0f766e' },
  Perdida: { bg: '#fee2e2', text: '#991b1b' },
  Rechazada: { bg: '#fce7f3', text: '#9d174d' },
  Cancelada: { bg: '#f3f4f6', text: '#374151' },
  Desierta: { bg: '#ffedd5', text: '#9a3412' },
  Borrador: { bg: '#f3f4f6', text: '#4b5563' },
};

const todayLocalYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const validateClosingDateNotFuture = (dateStr) => {
  if (!String(dateStr || '').trim()) return 'Indica la fecha de cierre.';
  if (String(dateStr).slice(0, 10) > todayLocalYmd()) {
    return 'La fecha de cierre no puede ser posterior a hoy.';
  }
  return '';
};

const parseLocalYmd = (raw) => {
  const s = String(raw || '').trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatLocalYmd = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Suma días hábiles (lun–vie). */
const addBusinessDays = (isoDate, days) => {
  const start = parseLocalYmd(isoDate) || new Date();
  let remaining = Math.max(0, Number(days) || 0);
  const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (remaining > 0) {
    current.setDate(current.getDate() + 1);
    const wd = current.getDay();
    if (wd !== 0 && wd !== 6) remaining -= 1;
  }
  return formatLocalYmd(current);
};

const formatMoney = (value, currency = 'CLP') => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency || 'CLP',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return String(amount);
  }
};

const formatDate = (value) => {
  const raw = String(value || '').trim().slice(0, 10);
  if (!raw) return '—';
  const [y, m, d] = raw.split('-');
  if (!y || !m || !d) return raw;
  return `${d}/${m}/${y}`;
};

const formatAddressLabel = (parts) =>
  [parts.address, parts.commune, parts.region].map((x) => String(x || '').trim()).filter(Boolean).join(', ');

const formatCommentsDisplay = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return '';
  return text
    .split(';')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
};

/**
 * Vista resumen de cotización emitida/cerrada (no el formulario completo de edición).
 */
export const QuotationSummaryForm = ({
  isOpen,
  onClose,
  quotation = null,
  items = [],
  isDarkMode = false,
  onEdit,
  onCloseQuotation,
  isClosing = false,
  contacts = [],
  organization = null,
  organizationUnits = [],
  onCreateContact,
  onCreateAddress,
  onRefreshOrgData,
  competitors = [],
  onCreateCompetitor,
  onRefreshCompetitors,
  users = [],
  paintSkuSet = null,
  upsellingSkuSet = null,
}) => {
  const ui = useUIColors();
  void isDarkMode;

  const [showClosePanel, setShowClosePanel] = useState(false);
  const [closeFlowPhase, setCloseFlowPhase] = useState('idle');
  const [itemsPanelOpen, setItemsPanelOpen] = useState(false);
  const [sidePanelView, setSidePanelView] = useState('items');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const [isBuildingPdfPreview, setIsBuildingPdfPreview] = useState(false);
  const [closeWizardStep, setCloseWizardStep] = useState(1);
  const [vendidaWizardStep, setVendidaWizardStep] = useState(1);
  const [closeStatus, setCloseStatus] = useState('Adjudicada');
  const [closingDate, setClosingDate] = useState(todayLocalYmd());
  const [lossReason, setLossReason] = useState('');
  const [lossBuyerComment, setLossBuyerComment] = useState('');
  const [competitorSelection, setCompetitorSelection] = useState('');
  const [showNewCompetitor, setShowNewCompetitor] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState('');
  const [competitorAmount, setCompetitorAmount] = useState('');
  const [lossCompetitorLines, setLossCompetitorLines] = useState({});
  const [closeError, setCloseError] = useState('');

  // Vendida fields
  const [orderCode, setOrderCode] = useState('');
  const [hasPurchaseOrder, setHasPurchaseOrder] = useState(true);
  const [poCode, setPoCode] = useState('');
  const [poDate, setPoDate] = useState(todayLocalYmd());
  const [poAcceptedDate, setPoAcceptedDate] = useState(todayLocalYmd());
  const [poAmount, setPoAmount] = useState('');
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const [buyerContactId, setBuyerContactId] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [createProductionRequest, setCreateProductionRequest] = useState(true);
  const [productionSelections, setProductionSelections] = useState({});
  const [productionNotes, setProductionNotes] = useState('');
  const [createUpsellingRequest, setCreateUpsellingRequest] = useState(true);
  const [upsellingSelections, setUpsellingSelections] = useState({});
  const [deliveryMethod, setDeliveryMethod] = useState('Despacho');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [scheduledDeliveryCode, setScheduledDeliveryCode] = useState('');
  const [deliveryCodeError, setDeliveryCodeError] = useState('');
  const [billingDocumentType, setBillingDocumentType] = useState('Boleta electrónica');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingGiro, setBillingGiro] = useState('');

  const [showNewContact, setShowNewContact] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [showNewDeliveryAddress, setShowNewDeliveryAddress] = useState(false);
  const [showNewBillingAddress, setShowNewBillingAddress] = useState(false);
  const [newAddressText, setNewAddressText] = useState('');
  const [newAddressTarget, setNewAddressTarget] = useState('delivery');
  const [savingExtra, setSavingExtra] = useState(false);

  const status = normalizeSummaryStatus(quotation?.status);
  const isAdjudicadaTransitional = status === 'Adjudicada';
  const isFinallyClosed = FINAL_CLOSE_STATUSES.includes(status);
  const canCloseQuotation = !isFinallyClosed;
  const closeStatusOptions = useMemo(
    () => (isAdjudicadaTransitional
      ? CLOSE_STATUS_OPTIONS.filter((opt) => opt.value !== 'Adjudicada')
      : CLOSE_STATUS_OPTIONS),
    [isAdjudicadaTransitional]
  );
  const statusStyle = STATUS_STYLE[status] || STATUS_STYLE.Emitida;

  const addressOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    const push = (label, value) => {
      const v = String(value || '').trim();
      if (!v || seen.has(v.toLowerCase())) return;
      seen.add(v.toLowerCase());
      opts.push({ value: v, label: label || v });
    };
    if (organization) {
      const orgAddr = formatAddressLabel(organization);
      if (orgAddr) push(`Casa matriz — ${orgAddr}`, orgAddr);
      else if (organization.address) push(`Casa matriz — ${organization.address}`, organization.address);
    }
    (organizationUnits || []).forEach((unit) => {
      const label = formatAddressLabel(unit);
      const name = String(unit.unit_name || 'Sucursal').trim();
      if (label) push(`${name} — ${label}`, label);
      else if (unit.address) push(`${name} — ${unit.address}`, unit.address);
    });
    const place = String(quotation?.delivery_place || '').trim();
    if (place) push(`Cotización — ${place}`, place);
    return opts;
  }, [organization, organizationUnits, quotation]);

  const orgContacts = useMemo(() => {
    const orgId = Number(quotation?.organization_id || organization?.id || 0);
    return (contacts || []).filter((c) => Number(c.organization_id) === orgId && c.is_active !== false);
  }, [contacts, quotation, organization]);

  // Reset close-form state only when the dialog opens or the quotation changes.
  // Do not depend on `organization`/`quotation` object identity: refreshing org/contacts
  // after adding a contact would otherwise collapse the close panel.
  useEffect(() => {
    if (!isOpen) return;
    setShowClosePanel(false);
    setCloseFlowPhase('idle');
    setItemsPanelOpen(false);
    setSidePanelView('items');
    setPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPdfPreviewError('');
    setIsBuildingPdfPreview(false);
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setCloseStatus(normalizeSummaryStatus(quotation?.status) === 'Adjudicada' ? 'Vendida' : 'Adjudicada');
    setClosingDate(todayLocalYmd());
    setLossReason(String(quotation?.loss_reason || '').trim());
    setLossBuyerComment(String(quotation?.loss_buyer_comment || '').trim());
    const parsedCompetitor = parseLossCompetitor(
      quotation?.loss_competitor || quotation?.loss_competitor_json
    );
    const competitorState = resolveCompetitorSelection(parsedCompetitor, competitors);
    setCompetitorSelection(competitorState.selection);
    setShowNewCompetitor(Boolean(competitorState.showNew));
    setNewCompetitorName(competitorState.newName);
    setCompetitorAmount(
      Number(parsedCompetitor.competitor_amount || 0) > 0
        ? String(parsedCompetitor.competitor_amount)
        : ''
    );
    setLossCompetitorLines(buildLossCompetitorLineMap(parsedCompetitor.line_items));
    setCloseError('');
    setOrderCode('');
    setHasPurchaseOrder(true);
    setPoCode('');
    setPoDate(todayLocalYmd());
    setPoAcceptedDate(todayLocalYmd());
    setPoAmount(String(quotation?.total_amount ?? ''));
    const matchedContact = (contacts || []).find(
      (c) => String(c.full_name || '').trim() === String(quotation?.contact_name || '').trim()
    );
    setBuyerContactId(matchedContact ? String(matchedContact.id) : '');
    setBuyerName(String(quotation?.contact_name || '').trim());
    setBuyerPhone(formatChileanMobile(matchedContact?.phone || matchedContact?.fono || ''));
    setBuyerEmail(String(matchedContact?.email || '').trim());
    setResponsibleUserId(quotation?.user_id ? String(quotation.user_id) : '');
    const initialDeliveryMethod = String(quotation?.delivery_method || 'Despacho').trim() || 'Despacho';
    setDeliveryMethod(initialDeliveryMethod);
    setDeliveryAddress(
      initialDeliveryMethod === 'Retiro en sucursal'
        ? ''
        : String(quotation?.delivery_place || '').trim()
    );
    setBillingDocumentType('Boleta electrónica');
    setBillingAddress('');
    setBillingGiro(String(organization?.industry || '').trim());
    setDispatchDate(todayLocalYmd());
    setExpectedDeliveryDate(
      addBusinessDays(todayLocalYmd(), Number(quotation?.delivery_days || 0))
    );
    setScheduledDeliveryCode('');
    setDeliveryCodeError('');
    setShowNewContact(false);
    setShowNewDeliveryAddress(false);
    setShowNewBillingAddress(false);
    setNewContactName('');
    setNewContactEmail('');
    setNewContactPhone('');
    setNewAddressText('');

    const { production, upselling } = partitionQuotationLineItems(
      (items || []).filter((item) => Number(item.quotation_id) === Number(quotation?.id)),
      paintSkuSet,
      upsellingSkuSet
    );
    setCreateProductionRequest(true);
    setProductionSelections(buildDefaultLineSelections(production));
    setProductionNotes('');
    setCreateUpsellingRequest(true);
    setUpsellingSelections(buildDefaultLineSelections(upselling));

    const existingSaleCode = String(quotation?.sale_code || '').trim();
    if (existingSaleCode) {
      setOrderCode(existingSaleCode);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/quotations/sales/next-code`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.sale_code) setOrderCode(String(data.sale_code));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset only on open / quotation id
  }, [isOpen, quotation?.id]);

  useEffect(() => {
    if (!showClosePanel) return;
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setCloseError('');
    if (closeStatus !== 'Perdida') {
      setShowNewCompetitor(false);
    }
  }, [showClosePanel, closeStatus]);

  useEffect(() => {
    const contactId = Number(buyerContactId || 0);
    if (!contactId) return;
    const contact = orgContacts.find((c) => Number(c.id) === contactId);
    if (!contact) return;
    setBuyerName(String(contact.full_name || '').trim());
    setBuyerPhone(formatChileanMobile(contact.phone || contact.fono || ''));
    setBuyerEmail(String(contact.email || '').trim());
  }, [buyerContactId, orgContacts]);

  useEffect(() => {
    if (!isOpen) return;
    const industry = String(organization?.industry || '').trim();
    if (industry) setBillingGiro((prev) => prev || industry);
  }, [isOpen, organization?.id, organization?.industry]);

  useEffect(() => {
    if (closeStatus !== 'Vendida') return;
    const baseDate = hasPurchaseOrder
      ? (String(poAcceptedDate || '').trim() || String(poDate || '').trim() || closingDate)
      : closingDate;
    setExpectedDeliveryDate(addBusinessDays(baseDate, Number(quotation?.delivery_days || 0)));
  }, [poAcceptedDate, poDate, closingDate, hasPurchaseOrder, closeStatus, quotation?.delivery_days]);

  const fetchNextDeliveryCode = async (saleCode) => {
    const code = String(saleCode || '').trim();
    if (!code) return '';
    const params = new URLSearchParams({ sale_code: code });
    const res = await authFetch(
      `${API_BASE_URL}/api/quotations/logistics/deliveries/next-code?${params.toString()}`
    );
    if (!res.ok) return '';
    const data = await res.json();
    return String(data?.delivery_code || '').trim();
  };

  const checkDeliveryCodeAvailable = async (deliveryCode) => {
    const code = String(deliveryCode || '').trim();
    if (!code) {
      return { available: false, detail: 'Indica el código de la entrega programada.' };
    }
    const params = new URLSearchParams({ code });
    const res = await authFetch(
      `${API_BASE_URL}/api/quotations/logistics/deliveries/check-code?${params.toString()}`
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      return { available: false, detail: errData.detail || `HTTP ${res.status}` };
    }
    return res.json();
  };

  useEffect(() => {
    if (closeStatus !== 'Vendida' || vendidaWizardStep !== 2) return undefined;
    const code = String(scheduledDeliveryCode || '').trim();
    if (!code) {
      setDeliveryCodeError('');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await checkDeliveryCodeAvailable(code);
        if (cancelled) return;
        if (!result.available) {
          setDeliveryCodeError(result.detail || 'Ya existe una entrega con ese código.');
        } else {
          setDeliveryCodeError('');
        }
      } catch {
        if (!cancelled) setDeliveryCodeError('');
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scheduledDeliveryCode, closeStatus, vendidaWizardStep]);

  useEffect(() => {
    if (deliveryMethod === 'Retiro en sucursal') {
      setDeliveryAddress('');
      return;
    }
    if (!deliveryAddress && addressOptions.length) {
      const fromQuote = String(quotation?.delivery_place || '').trim();
      setDeliveryAddress(fromQuote || addressOptions[0].value);
    }
  }, [addressOptions, deliveryAddress, deliveryMethod, quotation]);

  useEffect(() => {
    if (billingDocumentType !== 'Factura') return;
    if (!billingAddress && addressOptions.length) {
      setBillingAddress(addressOptions[0].value);
    }
  }, [addressOptions, billingAddress, billingDocumentType]);

  const lossReasonOptions = useMemo(() => getActiveQuotationLossReasonOptions(), []);

  const storedLossCompetitor = useMemo(
    () => parseLossCompetitor(quotation?.loss_competitor || quotation?.loss_competitor_json),
    [quotation?.loss_competitor, quotation?.loss_competitor_json]
  );

  const storedLossCompetitorLineMap = useMemo(
    () => buildLossCompetitorLineMap(storedLossCompetitor.line_items),
    [storedLossCompetitor]
  );

  const storedCompetitorDisplayName = useMemo(() => {
    const competitorId = Number(storedLossCompetitor.competitor_id || 0);
    if (competitorId > 0) {
      const match = (competitors || []).find((entry) => Number(entry.id) === competitorId);
      if (match?.name) return String(match.name).trim();
    }
    return String(storedLossCompetitor.competitor_name || '').trim();
  }, [competitors, storedLossCompetitor]);

  const lossReasonLabel = useMemo(() => {
    const code = String(quotation?.loss_reason || '').trim();
    if (!code) return '';
    const match = lossReasonOptions.find((opt) => opt.value === code);
    return match?.label || code;
  }, [lossReasonOptions, quotation?.loss_reason]);

  const updateLossCompetitorLine = (itemId, field, value) => {
    const key = Number(itemId);
    if (!key) return;
    setLossCompetitorLines((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveNewCompetitor = async () => {
    const name = String(newCompetitorName || '').trim();
    if (!name) {
      setCloseError('Indica el nombre de la nueva empresa competidora.');
      return;
    }
    if (!onCreateCompetitor) {
      setCloseError('No se puede crear la empresa competidora en este momento.');
      return;
    }
    setCloseError('');
    setSavingExtra(true);
    try {
      const created = await onCreateCompetitor({ name });
      const createdId = Number(created?.id || 0);
      if (!createdId) {
        throw new Error('No se pudo crear la empresa competidora.');
      }
      setCompetitorSelection(String(createdId));
      setShowNewCompetitor(false);
      setNewCompetitorName('');
      await onRefreshCompetitors?.();
    } catch (error) {
      setCloseError(error?.message || 'No se pudo crear la empresa competidora.');
    } finally {
      setSavingExtra(false);
    }
  };

  const lineItems = useMemo(() => {
    const qid = Number(quotation?.id || 0);
    const qcode = String(quotation?.quotation_code || '').trim();
    return (items || []).filter((item) => {
      if (qid && Number(item.quotation_id) === qid) return true;
      if (qcode && String(item.quotation_code || '').trim() === qcode) return true;
      return false;
    });
  }, [items, quotation]);

  const quotationTotals = useMemo(() => {
    const fromLines = (lineItems || []).reduce(
      (sum, item) => sum + Number(item.line_total || 0),
      0
    );
    const netTotal = Math.round(fromLines > 0 ? fromLines : Number(quotation?.total_amount || 0));
    const ivaTotal = Math.round(netTotal * 0.19);
    return {
      netTotal,
      ivaTotal,
      grossTotal: netTotal + ivaTotal,
    };
  }, [lineItems, quotation?.total_amount]);

  const storedCompetitorTotals = useMemo(
    () => calcOfferTotals(lineItems, storedLossCompetitorLineMap),
    [lineItems, storedLossCompetitorLineMap]
  );

  const storedAggregateVariation = useMemo(
    () => calcAggregateVariationPct(lineItems, storedLossCompetitorLineMap),
    [lineItems, storedLossCompetitorLineMap]
  );

  const commentsDisplay = useMemo(
    () => formatCommentsDisplay(quotation?.comments),
    [quotation?.comments]
  );

  const observationsDisplay = useMemo(
    () => String(quotation?.observations || '').trim(),
    [quotation?.observations]
  );

  const selectedContact = useMemo(() => {
    const contactId = Number(quotation?.contact_id || 0);
    return (contacts || []).find((c) => Number(c.id) === contactId) || null;
  }, [contacts, quotation?.contact_id]);

  const executiveUser = useMemo(() => {
    const userId = Number(quotation?.user_id || 0);
    return (users || []).find((u) => Number(u.id) === userId) || null;
  }, [users, quotation?.user_id]);

  const clientAddressForPdf = useMemo(() => {
    const fromOrg = [
      organization?.address,
      organization?.commune,
      organization?.region,
    ]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');
    return fromOrg || String(quotation?.delivery_place || '').trim();
  }, [organization, quotation?.delivery_place]);

  useEffect(() => {
    if (!itemsPanelOpen || sidePanelView !== 'pdf' || !isOpen || !quotation) return undefined;

    let cancelled = false;
    const run = async () => {
      setIsBuildingPdfPreview(true);
      setPdfPreviewError('');
      try {
        const url = await buildQuotationPdfObjectUrl({
          quotation_code: quotation.quotation_code || '',
          quotation_date: String(quotation.quotation_date || '').slice(0, 10),
          validity_days: quotation.validity_days,
          organization_name: quotation.organization_name,
          organization_rut: organization?.rut,
          client_address: clientAddressForPdf,
          contact_name: quotation.contact_name || selectedContact?.full_name,
          contact_email: selectedContact?.email,
          contact_phone: selectedContact?.phone || selectedContact?.fono,
          executive_name: quotation.user_full_name || executiveUser?.full_name,
          executive_email: executiveUser?.email,
          executive_phone: executiveUser?.fono || executiveUser?.phone,
          payment_terms: quotation.payment_terms,
          delivery_days: quotation.delivery_days,
          delivery_method: quotation.delivery_method || 'Despacho',
          delivery_place: quotation.delivery_place,
          channel: quotation.channel,
          tender: quotation.tender,
          comments: quotation.comments,
          items: lineItems,
        });
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setPdfPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (error) {
        if (!cancelled) {
          setPdfPreviewError(error?.message || 'No se pudo generar la previsualización PDF.');
        }
      } finally {
        if (!cancelled) setIsBuildingPdfPreview(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    itemsPanelOpen,
    sidePanelView,
    isOpen,
    quotation,
    lineItems,
    organization?.rut,
    clientAddressForPdf,
    selectedContact,
    executiveUser,
  ]);

  useEffect(() => () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
  }, [pdfPreviewUrl]);

  const updateProductionSelection = (itemId, field, value) => {
    setProductionSelections((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  const updateUpsellingSelection = (itemId, field, value) => {
    setUpsellingSelections((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  const validateLineSelections = (items, selectionMap, label) => {
    const enabled = (items || []).filter((item) => selectionMap[Number(item.id)]?.enabled);
    if (!enabled.length) return '';
    const invalid = enabled.find((item) => !(Number(selectionMap[Number(item.id)]?.quantity) > 0));
    if (invalid) return `Indica una cantidad válida para ${label}.`;
    return '';
  };

  const validateVendidaStep1 = () => {
    const closingMessage = validateClosingDateNotFuture(closingDate);
    if (closingMessage) return closingMessage;
    if (!String(orderCode || '').trim()) return 'Indica el código de venta.';
    if (!String(responsibleUserId || '').trim()) return 'Selecciona el ejecutivo responsable.';
    if (!String(deliveryMethod || '').trim()) return 'Indica el tipo de entrega.';
    if (deliveryMethod === 'Despacho' && !String(deliveryAddress || '').trim()) {
      return 'Indica la dirección de entrega.';
    }
    if (hasPurchaseOrder) {
      if (!String(poCode || '').trim()) return 'Indica el código de orden de compra.';
      if (!String(poDate || '').trim()) return 'Indica la fecha de recepción de la orden de compra.';
      if (!(Number(poAmount) > 0)) return 'Indica el monto de la OC.';
    }
    if (billingDocumentType === 'Factura' && !String(billingAddress || '').trim()) {
      return 'Indica la dirección de facturación.';
    }
    const phoneMessage = buyerPhone ? chileanMobileError(buyerPhone) : '';
    if (phoneMessage) return phoneMessage;
    return '';
  };

  const handleConfirmClose = async (overrides = {}) => {
    const skipProductionRequest = Object.prototype.hasOwnProperty.call(overrides, 'skipProductionRequest')
      ? Boolean(overrides.skipProductionRequest)
      : !createProductionRequest;
    const skipUpsellingRequest = Object.prototype.hasOwnProperty.call(overrides, 'skipUpsellingRequest')
      ? Boolean(overrides.skipUpsellingRequest)
      : !createUpsellingRequest;

    setCloseError('');
    if (!closeStatus) {
      setCloseError('Selecciona un estado de cierre.');
      return;
    }
    if (closeStatus !== 'Adjudicada') {
      const closingMessage = validateClosingDateNotFuture(closingDate);
      if (closingMessage) {
        setCloseError(closingMessage);
        return;
      }
    }
    if (closeStatus === 'Vendida') {
      const step1 = validateVendidaStep1();
      if (step1) {
        setCloseError(step1);
        return;
      }
      // Dates/code always required; line selections only when the request is not skipped.
      if (!String(dispatchDate || '').trim()) {
        setCloseError('Indica la fecha de despacho.');
        return;
      }
      if (!String(scheduledDeliveryCode || '').trim()) {
        setCloseError('Indica el código de la entrega programada.');
        return;
      }
      if (deliveryCodeError) {
        setCloseError(deliveryCodeError);
        return;
      }
      if (!skipProductionRequest) {
        const { production } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
        const step2 = validateLineSelections(production, productionSelections, 'producción');
        if (step2) {
          setCloseError(step2);
          return;
        }
      }
      if (!skipUpsellingRequest) {
        const { upselling } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
        const step3 = validateLineSelections(upselling, upsellingSelections, 'upselling');
        if (step3) {
          setCloseError(step3);
          return;
        }
      }
    }
    if (closeStatus === 'Perdida' && !String(lossReason || '').trim()) {
      setCloseError('Indica el motivo de pérdida.');
      return;
    }
    try {
      const options = {
        closing_date:
          closeStatus === 'Adjudicada' ? '' : String(closingDate).trim().slice(0, 10),
        sale_code: closeStatus === 'Vendida' ? String(orderCode).trim() : undefined,
        loss_reason: closeStatus === 'Perdida' ? String(lossReason).trim() : undefined,
        loss_buyer_comment: closeStatus === 'Perdida' ? String(lossBuyerComment).trim() : undefined,
      };
      if (closeStatus === 'Perdida') {
        const selectedId = Number(competitorSelection || 0) > 0 ? Number(competitorSelection) : 0;
        const selectedName = selectedId > 0
          ? String((competitors || []).find((entry) => Number(entry.id) === selectedId)?.name || '').trim()
          : (showNewCompetitor ? String(newCompetitorName || '').trim() : '');
        options.loss_competitor = buildLossCompetitorPayload({
          competitorId: selectedId,
          competitorName: selectedName,
          competitorAmount,
          lineMap: lossCompetitorLines,
          quotationItems: lineItems,
        });
      }
      if (closeStatus === 'Vendida') {
        options.has_purchase_order = Boolean(hasPurchaseOrder);
        if (hasPurchaseOrder) {
          options.purchase_order_code = String(poCode).trim();
          options.purchase_order_date = String(poDate).trim().slice(0, 10);
          options.purchase_order_accepted_date = String(poAcceptedDate || '').trim().slice(0, 10);
          options.purchase_order_amount = Number(poAmount);
        } else {
          options.purchase_order_code = '';
          options.purchase_order_date = '';
          options.purchase_order_accepted_date = '';
          options.purchase_order_amount = 0;
        }
        options.purchase_contact_name = String(buyerName || '').trim();
        options.purchase_contact_phone = formatChileanMobile(buyerPhone).trim();
        options.purchase_contact_email = String(buyerEmail || '').trim();
        options.assigned_user_id = Number(responsibleUserId || 0) || undefined;
        options.delivery_method = String(deliveryMethod).trim();
        options.delivery_address =
          deliveryMethod === 'Retiro en sucursal' ? '' : String(deliveryAddress).trim();
        options.delivery_date = String(dispatchDate).trim().slice(0, 10);
        options.expected_delivery_date = String(expectedDeliveryDate).trim().slice(0, 10);
        options.delivery_code = String(scheduledDeliveryCode).trim();
        options.billing_document_type = billingDocumentType;
        options.billing_address =
          billingDocumentType === 'Factura' ? String(billingAddress).trim() : '';
        options.billing_giro = billingDocumentType === 'Factura' ? String(billingGiro).trim() : '';
        const { production, upselling } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
        options.skip_production_request = skipProductionRequest;
        options.skip_upselling_request = skipUpsellingRequest;
        options.production_items = production;
        options.production_selections = skipProductionRequest
          ? buildClearedLineSelections(production)
          : productionSelections;
        options.production_notes = skipProductionRequest ? '' : String(productionNotes || '').trim();
        options.upselling_items = upselling;
        options.upselling_selections = skipUpsellingRequest
          ? buildClearedLineSelections(upselling)
          : upsellingSelections;
      }
      await onCloseQuotation?.(closeStatus, options);
      setShowClosePanel(false);
      setCloseFlowPhase('idle');
    } catch (error) {
      setCloseError(error?.message || 'No se pudo cerrar la cotización.');
    }
  };

  const validatePerdidaStep1 = () => {
    const closingMessage = validateClosingDateNotFuture(closingDate);
    if (closingMessage) return closingMessage;
    if (!String(lossReason || '').trim()) return 'Indica el motivo de pérdida.';
    return '';
  };

  const advanceVendidaFromStep2 = async ({ skipProduction }) => {
    const datesMessage = (() => {
      if (!String(dispatchDate || '').trim()) return 'Indica la fecha de despacho.';
      if (!String(scheduledDeliveryCode || '').trim()) return 'Indica el código de la entrega programada.';
      if (deliveryCodeError) return deliveryCodeError;
      return '';
    })();
    if (datesMessage) {
      setCloseError(datesMessage);
      return false;
    }
    const { production, upselling } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
    if (!skipProduction) {
      const linesMessage = validateLineSelections(production, productionSelections, 'producción');
      if (linesMessage) {
        setCloseError(linesMessage);
        return false;
      }
      setCreateProductionRequest(true);
    } else {
      setCreateProductionRequest(false);
      setProductionSelections(buildClearedLineSelections(production));
      setProductionNotes('');
    }
    try {
      const result = await checkDeliveryCodeAvailable(scheduledDeliveryCode);
      if (!result.available) {
        const err = result.detail || 'Ya existe una entrega con ese código.';
        setDeliveryCodeError(err);
        setCloseError(err);
        return false;
      }
      setDeliveryCodeError('');
    } catch (error) {
      setCloseError(error?.message || 'No se pudo validar el código de entrega.');
      return false;
    }
    setCreateUpsellingRequest(true);
    setUpsellingSelections(buildDefaultLineSelections(upselling));
    setVendidaWizardStep(3);
    return true;
  };

  const handleCloseWizardNext = async () => {
    setCloseError('');
    if (closeStatus === 'Vendida') {
      if (vendidaWizardStep === 1) {
        const message = validateVendidaStep1();
        if (message) {
          setCloseError(message);
          return;
        }
        try {
          const nextCode = await fetchNextDeliveryCode(orderCode);
          if (nextCode) {
            setScheduledDeliveryCode(nextCode);
            setDeliveryCodeError('');
          }
        } catch {
          /* ignore */
        }
        const { production } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
        setCreateProductionRequest(true);
        setProductionSelections(buildDefaultLineSelections(production));
        setVendidaWizardStep(2);
        return;
      }
      if (vendidaWizardStep === 2) {
        await advanceVendidaFromStep2({ skipProduction: false });
      }
      return;
    }
    const message = validatePerdidaStep1();
    if (message) {
      setCloseError(message);
      return;
    }
    setCloseWizardStep(2);
  };

  const handleCloseWizardSkipRequest = async () => {
    setCloseError('');
    if (closeStatus !== 'Vendida') return;
    if (vendidaWizardStep === 2) {
      await advanceVendidaFromStep2({ skipProduction: true });
      return;
    }
    if (vendidaWizardStep === 3) {
      const { upselling } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
      setCreateUpsellingRequest(false);
      setUpsellingSelections(buildClearedLineSelections(upselling));
      // Confirm after state flush: pass skip flags explicitly via a dedicated confirm path
      await handleConfirmClose({
        skipProductionRequest: !createProductionRequest,
        skipUpsellingRequest: true,
      });
    }
  };

  const handleCloseWizardBack = () => {
    setCloseError('');
    if (closeStatus === 'Vendida' && vendidaWizardStep > 1) {
      setVendidaWizardStep((step) => step - 1);
      return;
    }
    if (closeStatus === 'Perdida' && closeWizardStep > 1) {
      setCloseWizardStep(1);
      return;
    }
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setCloseFlowPhase('select-status');
  };

  const handleOpenStatusUpdate = () => {
    setCloseError('');
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setShowClosePanel(true);
    setCloseFlowPhase('select-status');
  };

  const handleSelectCloseStatus = (value) => {
    setCloseStatus(value);
    setCloseError('');
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setCloseFlowPhase('form');
  };

  const handleCancelClosePanel = () => {
    if (isClosing) return;
    setShowClosePanel(false);
    setCloseFlowPhase('idle');
    setCloseWizardStep(1);
    setVendidaWizardStep(1);
    setCloseError('');
  };

  const handleSaveNewContact = async () => {
    const name = String(newContactName || '').trim();
    if (!name) {
      setCloseError('Indica el nombre del nuevo contacto.');
      return;
    }
    const phoneMessage = chileanMobileError(newContactPhone);
    if (phoneMessage) {
      setCloseError(phoneMessage);
      return;
    }
    const orgId = Number(quotation?.organization_id || organization?.id || 0);
    if (!orgId) {
      setCloseError('La cotización no tiene organización asociada.');
      return;
    }
    setSavingExtra(true);
    setCloseError('');
    try {
      const created = await onCreateContact?.({
        organization_id: orgId,
        full_name: name,
        email: String(newContactEmail || '').trim(),
        phone: formatChileanMobile(newContactPhone).trim(),
        role: 'Comprador',
        status: 'Activo',
      });
      setBuyerName(name);
      if (created?.id) setBuyerContactId(String(created.id));
      setShowNewContact(false);
      setNewContactName('');
      setNewContactEmail('');
      setNewContactPhone('');
      await onRefreshOrgData?.();
    } catch (error) {
      setCloseError(error?.message || 'No se pudo crear el contacto.');
    } finally {
      setSavingExtra(false);
    }
  };

  const handleSaveNewAddress = async () => {
    const addr = String(newAddressText || '').trim();
    if (!addr) {
      setCloseError('Indica la nueva dirección.');
      return;
    }
    const orgId = Number(quotation?.organization_id || organization?.id || 0);
    if (!orgId) {
      setCloseError('La cotización no tiene organización asociada.');
      return;
    }
    setSavingExtra(true);
    setCloseError('');
    try {
      await onCreateAddress?.({
        organization_id: orgId,
        unit_name: `Dirección ${new Date().toLocaleDateString('es-CL')}`,
        address: addr,
        commune: organization?.commune || '',
        region: organization?.region || '',
        country: organization?.country || 'Chile',
      });
      if (newAddressTarget === 'billing') setBillingAddress(addr);
      else setDeliveryAddress(addr);
      setShowNewDeliveryAddress(false);
      setShowNewBillingAddress(false);
      setNewAddressText('');
      await onRefreshOrgData?.();
    } catch (error) {
      setCloseError(error?.message || 'No se pudo crear la dirección.');
    } finally {
      setSavingExtra(false);
    }
  };

  const inputStyle = {
    height: '36px',
    borderRadius: '8px',
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText || ui.text,
    padding: '0 10px',
    fontSize: '13px',
    fontWeight: 600,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const btnBase = {
    height: '36px',
    padding: '0 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 700,
    cursor: isClosing || savingExtra ? 'not-allowed' : 'pointer',
    opacity: isClosing || savingExtra ? 0.75 : 1,
  };

  const linkBtn = {
    border: 'none',
    background: 'transparent',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    alignSelf: 'flex-start',
  };

  if (!quotation) return null;

  const Field = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <span style={{ fontSize: '11px', fontWeight: 600, color: ui.muted }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, color: ui.text, wordBreak: 'break-word' }}>
        {value || '—'}
      </span>
    </div>
  );

  const renderQuotedItemsTable = ({ title = 'Ítems', inSidePanel = false } = {}) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: inSidePanel ? 0 : undefined,
        height: inSidePanel ? '100%' : undefined,
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text, marginBottom: '8px', flexShrink: 0 }}>
        {title} ({lineItems.length})
      </div>
      <div
        style={{
          overflowX: 'auto',
          overflowY: inSidePanel ? 'auto' : undefined,
          flex: inSidePanel ? 1 : undefined,
          border: `1px solid ${ui.border}`,
          borderRadius: '8px',
          minHeight: inSidePanel ? 0 : undefined,
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
          <thead>
            <tr style={{ background: ui.header }}>
              {['Producto', 'Color', 'Cant.', 'Precio neto', 'Total neto'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '8px 10px',
                    textAlign: h === 'Cant.' || h === 'Precio neto' || h === 'Total neto' ? 'right' : 'left',
                    fontSize: '11px',
                    color: ui.muted,
                    borderBottom: `1px solid ${ui.border}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: ui.muted, fontSize: '13px' }}>
                  Sin ítems
                </td>
              </tr>
            ) : (
              lineItems.map((item) => (
                <tr key={item.id || `${item.item_code}-${item.item_name}`}>
                  <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.text, borderBottom: `1px solid ${ui.border}` }}>
                    {item.item_name || item.product_name || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.muted, borderBottom: `1px solid ${ui.border}` }}>
                    {item.color || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}` }}>
                    {Number(item.quantity || 0).toLocaleString('es-CL')}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}` }}>
                    {formatMoney(item.unit_price, quotation.currency)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderBottom: `1px solid ${ui.border}` }}>
                    {formatMoney(item.line_total, quotation.currency)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const sidePanelTabStyle = (active) => ({
    flex: 1,
    padding: '8px 10px',
    borderRadius: '8px',
    border: `1px solid ${active ? ui.interactive : ui.border}`,
    background: active ? ui.interactive : ui.panel,
    color: active ? '#fff' : ui.text,
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  });

  const renderSidePanel = () => (
    <div
      style={{
        height: '100%',
        borderLeft: `1px solid ${ui.border}`,
        background: ui.sectionBg,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        minHeight: 0,
        padding: '12px',
      }}
    >
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => setSidePanelView('items')}
          style={sidePanelTabStyle(sidePanelView === 'items')}
        >
          Ítems
        </button>
        <button
          type="button"
          onClick={() => setSidePanelView('pdf')}
          style={sidePanelTabStyle(sidePanelView === 'pdf')}
        >
          PDF
        </button>
      </div>

      {sidePanelView === 'items' ? (
        renderQuotedItemsTable({ title: 'Ítems cotizados', inSidePanel: true })
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#ffffff',
          }}
        >
          {isBuildingPdfPreview ? (
            <div style={{ padding: 24, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
              Generando previsualización…
            </div>
          ) : null}
          {pdfPreviewError ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>
              {pdfPreviewError}
            </div>
          ) : null}
          {!isBuildingPdfPreview && !pdfPreviewError && pdfPreviewUrl ? (
            <iframe
              title={`PDF ${quotation.quotation_code || 'cotización'}`}
              src={pdfPreviewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
                background: '#ffffff',
                flex: 1,
                minHeight: 0,
              }}
            />
          ) : null}
          {!isBuildingPdfPreview && !pdfPreviewError && !pdfPreviewUrl ? (
            <div style={{ padding: 24, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
              Sin previsualización disponible.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  const renderTotalsAndNotes = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '12px',
        alignItems: 'start',
      }}
    >
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: '10px',
          padding: '12px',
          background: ui.panel,
          display: 'grid',
          gap: '8px',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text }}>Montos</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', color: ui.text }}>
          <span>Total neto</span>
          <span style={{ fontWeight: 600 }}>{formatMoney(quotationTotals.netTotal, quotation.currency)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', color: ui.text }}>
          <span>IVA (19%)</span>
          <span style={{ fontWeight: 600 }}>{formatMoney(quotationTotals.ivaTotal, quotation.currency)}</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            fontSize: '13px',
            color: ui.text,
            paddingTop: '8px',
            borderTop: `1px solid ${ui.border}`,
            fontWeight: 700,
          }}
        >
          <span>Total + IVA</span>
          <span>{formatMoney(quotationTotals.grossTotal, quotation.currency)}</span>
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: '10px',
          padding: '12px',
          background: ui.panel,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          minWidth: 0,
        }}
      >
        <div>
          <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text, marginBottom: '6px' }}>
            Notas compartidas
          </div>
          <div
            style={{
              fontSize: '12px',
              color: commentsDisplay ? ui.text : ui.muted,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {commentsDisplay || '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text, marginBottom: '6px' }}>
            Observaciones internas
          </div>
          <div
            style={{
              fontSize: '12px',
              color: observationsDisplay ? ui.text : ui.muted,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {observationsDisplay || '—'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStatusPicker = () => (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: '10px',
        padding: '14px',
        background: ui.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div>
        <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text }}>
          Actualizar estado
        </div>
        <div style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45, marginTop: '4px' }}>
          {isAdjudicadaTransitional
            ? 'Selecciona el estado final de esta cotización adjudicada.'
            : 'Selecciona el nuevo estado de la cotización para continuar.'}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {closeStatusOptions.map((opt) => {
          const optionStyle = STATUS_STYLE[opt.value] || STATUS_STYLE.Borrador;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={isClosing}
              onClick={() => handleSelectCloseStatus(opt.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                border: `1px solid ${ui.border}`,
                background: ui.sectionBg,
                cursor: isClosing ? 'default' : 'pointer',
                textAlign: 'left',
                opacity: isClosing ? 0.75 : 1,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    alignItems: 'center',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background: optionStyle.bg,
                    color: optionStyle.text,
                  }}
                >
                  {opt.label}
                </span>
                <span style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
                  {CLOSE_STATUS_DESCRIPTIONS[opt.value] || ''}
                </span>
              </div>
              <ChevronRight size={18} color={ui.muted} style={{ flexShrink: 0 }} />
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '4px' }}>
        <button
          type="button"
          disabled={isClosing}
          onClick={handleCancelClosePanel}
          style={{
            ...btnBase,
            border: `1px solid ${ui.border}`,
            background: ui.panel,
            color: ui.text,
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );

  const renderNonPerdidaCloseFields = () => {
    if (closeStatus === 'Adjudicada') return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: ui.muted }}>Fecha de cierre</span>
          <input
            type="date"
            value={closingDate}
            max={todayLocalYmd()}
            onChange={(e) => setClosingDate(e.target.value)}
            disabled={isClosing}
            style={inputStyle}
          />
        </label>
      </div>
    );
  };

  const renderVendidaCloseFields = () => (
    <QuotationVendidaCloseWizard
      ui={ui}
      quotation={quotation}
      lineItems={lineItems}
      users={users}
      paintSkuSet={paintSkuSet}
      upsellingSkuSet={upsellingSkuSet}
      vendidaWizardStep={vendidaWizardStep}
      closingDate={closingDate}
      setClosingDate={setClosingDate}
      maxClosingDate={todayLocalYmd()}
      orderCode={orderCode}
      setOrderCode={setOrderCode}
      responsibleUserId={responsibleUserId}
      setResponsibleUserId={setResponsibleUserId}
      buyerContactId={buyerContactId}
      setBuyerContactId={setBuyerContactId}
      buyerPhone={buyerPhone}
      setBuyerPhone={setBuyerPhone}
      buyerEmail={buyerEmail}
      setBuyerEmail={setBuyerEmail}
      hasPurchaseOrder={hasPurchaseOrder}
      setHasPurchaseOrder={setHasPurchaseOrder}
      poCode={poCode}
      setPoCode={setPoCode}
      poDate={poDate}
      setPoDate={setPoDate}
      poAcceptedDate={poAcceptedDate}
      setPoAcceptedDate={setPoAcceptedDate}
      poAmount={poAmount}
      setPoAmount={setPoAmount}
      deliveryMethod={deliveryMethod}
      setDeliveryMethod={setDeliveryMethod}
      deliveryAddress={deliveryAddress}
      setDeliveryAddress={setDeliveryAddress}
      dispatchDate={dispatchDate}
      setDispatchDate={setDispatchDate}
      scheduledDeliveryCode={scheduledDeliveryCode}
      setScheduledDeliveryCode={setScheduledDeliveryCode}
      deliveryCodeError={deliveryCodeError}
      expectedDeliveryDate={expectedDeliveryDate}
      billingDocumentType={billingDocumentType}
      setBillingDocumentType={setBillingDocumentType}
      billingAddress={billingAddress}
      setBillingAddress={setBillingAddress}
      billingGiro={billingGiro}
      setBillingGiro={setBillingGiro}
      organization={organization}
      orgContacts={orgContacts}
      addressOptions={addressOptions}
      productionSelections={productionSelections}
      updateProductionSelection={updateProductionSelection}
      productionNotes={productionNotes}
      setProductionNotes={setProductionNotes}
      upsellingSelections={upsellingSelections}
      updateUpsellingSelection={updateUpsellingSelection}
      showNewContact={showNewContact}
      setShowNewContact={setShowNewContact}
      newContactName={newContactName}
      setNewContactName={setNewContactName}
      newContactEmail={newContactEmail}
      setNewContactEmail={setNewContactEmail}
      newContactPhone={newContactPhone}
      setNewContactPhone={setNewContactPhone}
      onSaveNewContact={handleSaveNewContact}
      showNewDeliveryAddress={showNewDeliveryAddress}
      setShowNewDeliveryAddress={setShowNewDeliveryAddress}
      showNewBillingAddress={showNewBillingAddress}
      setShowNewBillingAddress={setShowNewBillingAddress}
      setNewAddressTarget={setNewAddressTarget}
      newAddressText={newAddressText}
      setNewAddressText={setNewAddressText}
      onSaveNewAddress={handleSaveNewAddress}
      savingExtra={savingExtra}
      isClosing={isClosing}
      inputStyle={inputStyle}
      btnBase={btnBase}
      linkBtn={linkBtn}
      formatMoney={formatMoney}
    />
  );

  const closeModeActive = showClosePanel && canCloseQuotation;
  const itemsPanelWidth = QUOTATION_PREVIEW_SIDE_PANEL_WIDTH;
  const toggleHalfWidth = 12;
  const baseFormWidth = closeModeActive ? 900 : 880;
  const formPanelWidth = itemsPanelOpen ? 720 : baseFormWidth;
  const modalWidth = itemsPanelOpen ? formPanelWidth + itemsPanelWidth : formPanelWidth;

  const sideToggleStyles = {
    position: 'absolute',
    top: '50%',
    right: (itemsPanelOpen ? itemsPanelWidth : 0) - toggleHalfWidth,
    transform: 'translateY(-50%)',
    width: '24px',
    height: '80px',
    backgroundColor: ui.panel,
    border: `2px solid ${ui.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: ui.text,
    zIndex: 20,
    boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
    padding: 0,
  };

  const statusBadge = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 700,
        background: statusStyle.bg,
        color: statusStyle.text,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );

  const modalFooter = !closeModeActive ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
      {canCloseQuotation ? (
        <button
          type="button"
          disabled={isClosing}
          onClick={handleOpenStatusUpdate}
          style={{
            ...btnBase,
            border: 'none',
            background: '#0f766e',
            color: '#fff',
          }}
        >
          Actualizar estado
        </button>
      ) : null}
      {onEdit ? (
        <button
          type="button"
          disabled={isClosing}
          onClick={() => onEdit(quotation)}
          style={{
            ...btnBase,
            border: `1px solid ${ui.border}`,
            background: ui.panel,
            color: ui.text,
          }}
        >
          Editar
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isClosing) onClose?.();
      }}
      title={`Cotización ${quotation.quotation_code || ''}`.trim()}
      subtitle=""
      size={itemsPanelOpen || closeModeActive ? 'xxl' : 'lg'}
      isDarkMode={isDarkMode}
      headerActions={statusBadge}
      footer={modalFooter}
      style={{
        width: `${modalWidth}px`,
        maxWidth: 'calc(100vw - 32px)',
      }}
      sidePanelWidth={itemsPanelWidth}
      sidePanel={itemsPanelOpen ? renderSidePanel() : null}
      edgeControl={(
        <button
          type="button"
          onClick={() => setItemsPanelOpen((v) => !v)}
          style={sideToggleStyles}
          title={
            itemsPanelOpen
              ? 'Ocultar panel lateral'
              : `Mostrar ${sidePanelView === 'pdf' ? 'PDF' : 'ítems'}`
          }
          aria-label={
            itemsPanelOpen
              ? 'Ocultar panel lateral'
              : `Mostrar ${sidePanelView === 'pdf' ? 'PDF' : 'ítems'}`
          }
        >
          {itemsPanelOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {!closeModeActive ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px 16px',
                padding: '12px',
                borderRadius: '10px',
                border: `1px solid ${ui.border}`,
                background: ui.panel,
              }}
            >
              <Field label="Empresa" value={quotation.organization_name} />
              <Field label="Contacto" value={quotation.contact_name} />
              <Field label="Ejecutivo" value={quotation.user_full_name} />
              <Field label="Fecha" value={formatDate(quotation.quotation_date)} />
              <Field label="Entrega" value={`${Number(quotation.delivery_days ?? 0)} días`} />
              <Field label="Tipo de entrega" value={quotation.delivery_method || 'Despacho'} />
              <Field label="Lugar de entrega" value={quotation.delivery_place} />
              <Field label="Pago" value={quotation.payment_terms} />
              <Field label="Licitación" value={quotation.tender} />
              <Field label="Canal" value={quotation.channel} />
            </div>

            {renderTotalsAndNotes()}
          </>
        ) : null}

        {status === 'Perdida' && !closeModeActive ? (
          <div
            style={{
              border: `1px solid ${ui.border}`,
              borderRadius: '10px',
              padding: '12px',
              background: ui.panel,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 750, color: ui.text }}>
              Análisis de competencia
            </div>
            {lossReasonLabel ? (
              <Field label="Motivo de pérdida" value={lossReasonLabel} />
            ) : null}
            {quotation?.loss_buyer_comment ? (
              <Field label="Comentario del comprador" value={quotation.loss_buyer_comment} />
            ) : null}
            {lossCompetitorHasData(storedLossCompetitor) ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '12px 16px',
                  }}
                >
                  <Field label="Empresa competidora" value={storedCompetitorDisplayName || '—'} />
                  <Field
                    label="Monto ofertado"
                    value={
                      Number(storedLossCompetitor.competitor_amount || 0) > 0
                        ? formatLossCompetitorMoney(storedLossCompetitor.competitor_amount, quotation.currency)
                        : '—'
                    }
                  />
                </div>
                {lineItems.length > 0 ? (
                  <div style={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '940px' }}>
                      <thead>
                        <tr style={{ background: ui.header }}>
                          {[
                            'Nuestro producto',
                            'Nuestro precio unit. neto',
                            'Producto competencia',
                            'Precio unit. neto competencia',
                            'Subtotal neto competencia',
                            'Variación %',
                          ].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: '8px 10px',
                                textAlign: h === 'Nuestro producto' || h === 'Producto competencia' ? 'left' : 'right',
                                fontSize: '11px',
                                color: ui.muted,
                                borderBottom: `1px solid ${ui.border}`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item) => {
                          const row = storedLossCompetitorLineMap[Number(item.id)] || {};
                          const hasRowData = Boolean(
                            String(row.product || '').trim() || Number(row.unitPrice || 0) > 0
                          );
                          if (!hasRowData) return null;
                          const qty = Number(item.quantity || 0);
                          const ourUnit = Number(item.unit_price || 0);
                          const competitorUnit = Number(row.unitPrice || 0);
                          const subtotal = calcCompetitorLineSubtotal(qty, competitorUnit);
                          const variation = calcPriceVariationPct(ourUnit, competitorUnit);
                          return (
                            <tr key={item.id || `${item.item_code}-${item.item_name}`}>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.text, borderBottom: `1px solid ${ui.border}` }}>
                                <div style={{ fontWeight: 600 }}>{item.item_name || item.product_name || '—'}</div>
                                <div style={{ fontSize: '11px', color: ui.muted }}>
                                  Cant. {qty.toLocaleString('es-CL')}
                                </div>
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                                {ourUnit > 0 ? formatLossCompetitorMoney(ourUnit, quotation.currency) : '—'}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.muted, borderBottom: `1px solid ${ui.border}` }}>
                                {row.product || '—'}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                                {competitorUnit > 0 ? formatLossCompetitorMoney(competitorUnit, quotation.currency) : '—'}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                                {competitorUnit > 0 ? formatLossCompetitorMoney(subtotal, quotation.currency) : '—'}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                                {formatVariationPct(variation)}
                              </td>
                            </tr>
                          );
                        })}
                        <tr style={{ background: ui.sectionBg }}>
                          <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, color: ui.text, borderTop: `1px solid ${ui.border}` }}>
                            Totales ofertados
                          </td>
                          <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderTop: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: '10px', color: ui.muted, fontWeight: 600 }}>Nosotros</div>
                            {formatLossCompetitorMoney(storedCompetitorTotals.ourTotal, quotation.currency)}
                          </td>
                          <td style={{ padding: '10px', borderTop: `1px solid ${ui.border}` }} />
                          <td style={{ padding: '10px', borderTop: `1px solid ${ui.border}` }} />
                          <td style={{ padding: '10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', borderTop: `1px solid ${ui.border}`, whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: '10px', color: ui.muted, fontWeight: 600 }}>Competencia</div>
                            {storedCompetitorTotals.competitorTotal > 0
                              ? formatLossCompetitorMoney(storedCompetitorTotals.competitorTotal, quotation.currency)
                              : '—'}
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 800, textAlign: 'right', borderTop: `1px solid ${ui.border}`, color: ui.text, whiteSpace: 'nowrap' }}>
                            {formatVariationPct(storedAggregateVariation)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </>
            ) : (
              !lossReasonLabel && !quotation?.loss_buyer_comment ? (
                <div style={{ fontSize: '12px', color: ui.muted }}>
                  Sin datos de competencia registrados.
                </div>
              ) : null
            )}
          </div>
        ) : null}


        {closeModeActive && closeFlowPhase === 'select-status' ? renderStatusPicker() : null}

        {closeModeActive && closeFlowPhase === 'form' ? (
          <QuotationCloseWizard
            ui={ui}
            quotation={quotation}
            lineItems={lineItems}
            competitors={competitors}
            isAdjudicadaTransitional={isAdjudicadaTransitional}
            closeStatus={closeStatus}
            closeWizardStep={closeWizardStep}
            closingDate={closingDate}
            setClosingDate={setClosingDate}
            lossReason={lossReason}
            setLossReason={setLossReason}
            lossReasonOptions={lossReasonOptions}
            lossBuyerComment={lossBuyerComment}
            setLossBuyerComment={setLossBuyerComment}
            competitorSelection={competitorSelection}
            setCompetitorSelection={setCompetitorSelection}
            showNewCompetitor={showNewCompetitor}
            setShowNewCompetitor={setShowNewCompetitor}
            newCompetitorName={newCompetitorName}
            setNewCompetitorName={setNewCompetitorName}
            competitorAmount={competitorAmount}
            setCompetitorAmount={setCompetitorAmount}
            lossCompetitorLines={lossCompetitorLines}
            updateLossCompetitorLine={updateLossCompetitorLine}
            onSaveNewCompetitor={onCreateCompetitor ? handleSaveNewCompetitor : null}
            closeError={closeError}
            isClosing={isClosing}
            savingExtra={savingExtra}
            onCancel={handleCancelClosePanel}
            onBack={handleCloseWizardBack}
            onNext={handleCloseWizardNext}
            onSkipRequest={handleCloseWizardSkipRequest}
            onConfirm={() => handleConfirmClose()}
            formatMoney={formatMoney}
            inputStyle={inputStyle}
            btnBase={btnBase}
            renderNonPerdidaFields={renderNonPerdidaCloseFields}
            renderVendidaFields={renderVendidaCloseFields}
            vendidaWizardStep={vendidaWizardStep}
            maxClosingDate={todayLocalYmd()}
          />
        ) : null}


      </div>
    </Modal>
  );
};

export default QuotationSummaryForm;
